/**
 * ErrorBoundary — wraps every route (never individual components).
 * monitoringSKILL Rule 9: reports to Sentry with component + route context.
 * state-managementSKILL Rule 4: ErrorBoundary wraps every route — not individual components.
 * state-managementSKILL Rule 5: Sentry.captureException always called in componentDidCatch.
 */
import { Component, type ReactNode, type ErrorInfo } from 'react'
import * as Sentry from '@sentry/react'
import { AlertTriangle } from 'lucide-react'

// ── Types ─────────────────────────────────────────────────────────────────────

interface Props {
  children:  ReactNode
  route?:    string     // e.g. '/practice' — used in Sentry tags for triage
  fallback?: ReactNode  // optional custom fallback UI
}

interface State {
  hasError: boolean
  eventId:  string | null
}

// ── Class component (required — React error boundaries need lifecycle methods) ─

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, eventId: null }

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true }
  }

  // Rule 5 — always report to Sentry with context
  componentDidCatch(error: Error, info: ErrorInfo): void {
    const eventId = Sentry.captureException(error, {
      extra: {
        componentStack: info.componentStack,
        route:          this.props.route ?? 'unknown',
      },
      tags: {
        error_boundary: 'true',
        route:          this.props.route ?? 'unknown',
      },
    })
    this.setState({ eventId })
  }

  handleReset = (): void => {
    this.setState({ hasError: false, eventId: null })
    window.location.href = '/home'
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children
    if (this.props.fallback)  return this.props.fallback

    return (
      <div
        role="alert"
        className="min-h-screen flex flex-col items-center justify-center
                   bg-page px-6 py-16 text-center"
      >
        <div className="w-16 h-16 rounded-full bg-error-light flex items-center justify-center mb-6">
          <AlertTriangle className="w-8 h-8 text-error" aria-hidden="true" />
        </div>

        <h1 className="text-2xl font-bold text-text-primary mb-2 font-heading">
          Something went wrong
        </h1>
        <p className="text-text-body max-w-sm mb-8 text-sm leading-relaxed">
          We hit an unexpected error. Your progress has been saved.
          {this.state.eventId != null && (
            <span className="block mt-2 text-xs text-text-muted">
              Error ID: {this.state.eventId}
            </span>
          )}
        </p>

        <button
          onClick={this.handleReset}
          className="btn-primary px-6 py-3 min-h-[44px]"
        >
          Go to Home
        </button>
      </div>
    )
  }
}

// ── Convenience wrapper for per-route usage ───────────────────────────────────

export function RouteErrorBoundary({
  children,
  route,
}: {
  children: ReactNode
  route:    string
}) {
  return <ErrorBoundary route={route}>{children}</ErrorBoundary>
}
